import { useNavigate, useLocation } from 'react-router-dom';
import type { components } from '@bookingdei/api-client/types';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { api } from '../api';
import { brand } from '../theme/tokens';
import { useLanguage } from './language-context';
import { useFavorites } from './favorites-context';
import { useResource } from './use-resource';

type Business = components['schemas']['Business'];

// The mobile inner app bar (design DC:211-220, titles DC:4758-4762; CB-17 website-only
// `inner-app-bar.tsx`). Every mobile route except home gets this bar instead of the
// lockup: a round ‹ back button, the page title centred, and a contextual action on the
// right — the save heart on a business page, an empty 38 px spacer elsewhere so the
// title stays optically centred.

// The business routes whose title is the business's own name and whose action is ♡.
const BUSINESS_RE = /^\/(?:business|b|l)\/([^/]+)\/?$/;

// Route → title key. Order matters: the first matching prefix wins.
const TITLES: Array<[RegExp, MessageKey]> = [
  [/^\/offer\b/, 'offers.title'],
  [/^\/trending\b/, 'tabs.trending'],
  [/^\/videos\b/, 'nav.videos'],
  [/^\/search\b/, 'search.title'],
  [/^\/(?:business|b)\/[^/]+\/book\b/, 'bkflow.appbar_title'],
  [/^\/booking\//, 'appbar.booking_confirmed'],
  [/^\/login\b/, 'auth.signin_title'],
  [/^\/signup\b/, 'auth.create_title'],
  [/^\/merchant-signup\b/, 'nav.for_business'],
  [/^\/for-business\b/, 'nav.for_business'],
  [/^\/pricing\b/, 'nav.pricing'],
  [/^\/post-an-offer\b/, 'nav.post_offer'],
  [/^\/get-featured\b/, 'nav.get_featured'],
  [/^\/blog\b/, 'nav.blog'],
  [/^\/help\b/, 'help.title'],
  [/^\/contact\b/, 'appbar.contact'],
  [/^\/about\b/, 'footer.about'],
  [/^\/status\b/, 'appbar.status'],
  [/^\/terms\b/, 'appbar.terms'],
  [/^\/privacy\b/, 'appbar.privacy'],
  [/^\/apps?\b/, 'app.title'],
];

// The title key for a pathname, or null when the design falls back to the brand name
// (a business page before its name loads, not-found, anything unlisted).
export function innerTitleKey(pathname: string): MessageKey | null {
  for (const [re, key] of TITLES) if (re.test(pathname)) return key;
  return null;
}

// The slug when the pathname is a business page (the ♡ action applies), else null.
export function businessSlugOf(pathname: string): string | null {
  const m = pathname.match(BUSINESS_RE);
  return m ? decodeURIComponent(m[1]!) : null;
}

export function InnerAppBar() {
  const { t, lang } = useLanguage();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isSaved, toggle } = useFavorites();
  const slug = businessSlugOf(pathname);

  const business = useResource<Business | null>(
    (signal) =>
      slug ? (api.GET('/businesses/{slug}', { params: { path: { slug } }, signal, lang }) as Promise<Business>) : Promise.resolve(null),
    [slug, lang],
  );
  const biz = slug ? business.data : null;

  const key = innerTitleKey(pathname);
  const title = biz ? (lang === 'bn' && biz.name_bn) || biz.name : key ? t(key) : brand.name;

  function back() {
    // React Router records its own history index; 0 means this is the first entry, so
    // there is nothing of ours to go back to — go home rather than leave the site.
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate('/');
  }

  const saved = biz ? isSaved(biz.id) : false;

  return (
    <div className="ibar">
      <button type="button" className="ibar__btn" aria-label={t('action.back')} onClick={back}>
        <span aria-hidden="true">‹</span>
      </button>
      <div className="ibar__title">{title}</div>
      {biz ? (
        <button
          type="button"
          className={saved ? 'ibar__btn ibar__btn--saved' : 'ibar__btn'}
          aria-pressed={saved}
          aria-label={t(saved ? 'common.saved' : 'common.save')}
          onClick={() => toggle(biz.id)}
        >
          <span aria-hidden="true">{saved ? '♥' : '♡'}</span>
        </button>
      ) : (
        <span className="ibar__spacer" aria-hidden="true" />
      )}
    </div>
  );
}
